import {DestroyRef, Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DragulaService} from 'ng2-dragula';
import {retry, share, tap} from 'rxjs/operators';
import {Command} from 'src/app/commands/commands.template';
import {MxCell} from 'src/app/editor-interface/shared/models/mx-cell.interface';
import {
  DragulaEvent,
  DRAGULA_BAG_OPTIONS,
  DRAGULA_BAG_PREFIXES
} from 'src/app/shared/models/dragula.interface';
import {DragulaConfigurationService} from 'src/app/shared/service/dragula-configuration.service';
import {ResourceCommands} from '../../commands/resource/resource.commands';
import {UndoService} from '../../commands/undo.service';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';

@Injectable()
export class DragAssistService {
  private r2: Renderer2;

  constructor(
    private dragulaSvc: DragulaService,
    private dragulaConfigurationService: DragulaConfigurationService,
    private graphHelperSvc: GraphHelperService,
    private resourceCommands: ResourceCommands,
    private undoSvc: UndoService,
    private destroyRef: DestroyRef,
    r2f: RendererFactory2
  ) {
    this.r2 = r2f.createRenderer(null, null);
    this.init();
  }

  private init(): void {
    this.initDragulaBag();
    this.initDragulaDrop();
    this.initDragulaDrag();
    this.initDragulaDragend();
  }

  private initDragulaBag(): void {
    const groupName = DRAGULA_BAG_PREFIXES.ACTIVITY;
    const options = DRAGULA_BAG_OPTIONS[groupName];

    this.dragulaConfigurationService.createGroup({
      groupName,
      ...options
    });
  }

  private initDragulaDrop(): void {
    this.dragulaSvc
      .drop(DRAGULA_BAG_PREFIXES.ACTIVITY)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(data => this.onDrop(data)),
        retry(),
        share()
      )
      .subscribe();
  }
  private initDragulaDrag(): void {
    this.dragulaSvc
      .drag(DRAGULA_BAG_PREFIXES.ACTIVITY)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(data => setTimeout(() => this.setShadowStyle(data))),
        tap(data => this.beginDrag(data)),
        retry(),
        share()
      )
      .subscribe();
  }
  private initDragulaDragend(): void {
    this.dragulaSvc
      .dragend(DRAGULA_BAG_PREFIXES.ACTIVITY)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.endDrag()),
        retry(),
        share()
      )
      .subscribe();
  }

  public setShadowStyle({el}: DragulaEvent<'drag'>) {
    const root = document.querySelector('.resource.gu-mirror') as HTMLElement;

    if (root && el instanceof HTMLElement && el.dataset.dragShadow) {
      const selfDestructMouseUpHandler = function (evt) {
        evt['skipGraphHandlers'] = true;
        root.removeEventListener('mouseup', selfDestructMouseUpHandler);
      };

      root.addEventListener('mouseup', selfDestructMouseUpHandler);
      const styleObj = JSON.parse(el.dataset.dragShadow);

      if (styleObj.transform) {
        const factor = Number(styleObj.transform.match(/scale.([0-9.]+)./).pop());

        this.r2.setStyle(root, 'height', `${Number(root.style.height.match(/[0-9]+/)) / factor}px`);
        this.r2.setStyle(root, 'width', `${Number(root.style.width.match(/[0-9]+/)) / factor}px`);
        styleObj.transform = `scale(${factor})`;
      }

      for (const key in styleObj) {
        if (styleObj.hasOwnProperty(key)) {
          this.r2.setStyle(root, key, styleObj[key]);
        }
      }
    }
  }

  public beginDrag({el}: DragulaEvent<'drag'>): void {
    if (el instanceof HTMLElement && el.dataset.dragMetadata) {
      const metaData: ResourceCommandInput = JSON.parse(el.dataset.dragMetadata);
      const resId = metaData.resource.resourceDbId;
      const actId = metaData.srcId;
      const isCopied = Boolean(Number(el.dataset.isCopied));

      if (resId && actId) {
        const actArr = this.graphHelperSvc.allActivities();

        actArr.forEach((cell: MxCell) => cell.comp.instance.markDropArea(resId, actId, isCopied));
      }
    }
  }

  public endDrag(): void {
    const actArr = this.graphHelperSvc.allActivities();

    actArr.forEach((cell: MxCell) => cell.comp.instance.clearDropArea());
  }

  public onDrop({target, el, sibling, source}: DragulaEvent<'drop'>): void {
    if (el instanceof HTMLElement && target !== null) {
      const commandData: ResourceCommandInput = JSON.parse(el.dataset.dragMetadata);
      const isCopied = Boolean(Number(el.dataset.isCopied));
      const tgtChildren = Array.from(target.children || []);
      let command: Command;

      if (target === source) {
        const args = {
          activityMxKey: commandData.srcId,
          resourceMxKey: commandData.resource.mxKey,
          oldPosition: commandData.srcPos + 1,
          newPosition: tgtChildren.indexOf(el) + 1
        };

        command = this.resourceCommands.swap(args);
      } else if (isCopied) {
        const targetId = Number(target.attributes['act-id'].nodeValue);
        const args = {
          resource: commandData.resource,
          position: tgtChildren.indexOf(sibling) + 1,
          mxKey: targetId,
          cells: []
        };

        // commandData.tgtId = targetId;
        // el.dataset.dragMetadata = JSON.stringify(commandData);
        command = this.resourceCommands.attach(args);
      } else {
        const targetId = Number(target.attributes['act-id'].nodeValue);
        const args = {
          resource: commandData.resource,
          position: tgtChildren.indexOf(sibling) + 1,
          oldPosition: commandData.srcPos + 1,
          tgtMxKey: targetId,
          srcMxKey: commandData.srcId
        };

        // commandData.tgtId = targetId;
        // el.dataset.dragMetadata = JSON.stringify(commandData);
        command = this.resourceCommands.move(args);
      }

      this.undoSvc.executeCommand(command);
    }
  }
}

export interface ResourceCommandInput {
  resource: {
    resourceDbId: string;
    name: string;
    type: string;
    mxKey: number;
    hidden?: boolean;
  };
  srcId: number;
  srcPos: number;
  tgtId?: number;
  tgtPos?: number;
}
